import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useTranslation } from 'react-i18next';
import {
  fetchDashboardData,
  setDateRange,
} from '../redux/slices/dashboardSlice';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ScatterChart,
  Scatter,
  ZAxis,
} from 'recharts';
import ExpensesList from '../components/expenses/ExpensesList';

const COLORS = ['#0b5b8a', '#4ccbd9', '#0e3ce3', '#3f9cb52'];

// Palette de bleus
const BLUE_PALETTE = {
  DARK: '#0b5b8a', // Bleu foncé
  MEDIUM: '#2a6f97', // Bleu marine
  LIGHT: '#4ccbd9', // Bleu clair
  ELECTRIC: '#0e3ce3', // Bleu électrique
  PASTEL: '#89c2d9', // Bleu pastel
  DEEP: '#012a4a', // Bleu profond
};

// Utilisation des couleurs pour les différents graphiques
const CHART_COLORS = {
  incomes: [BLUE_PALETTE.DARK, BLUE_PALETTE.LIGHT],
  accounts: [
    BLUE_PALETTE.DARK,
    BLUE_PALETTE.LIGHT,
    BLUE_PALETTE.ELECTRIC,
    BLUE_PALETTE.MEDIUM,
  ],
  clientProjects: [BLUE_PALETTE.DARK, BLUE_PALETTE.LIGHT],
  internalProjects: [BLUE_PALETTE.DEEP, BLUE_PALETTE.PASTEL],
  hoursByUser: [BLUE_PALETTE.ELECTRIC, BLUE_PALETTE.MEDIUM],
};

const DashboardPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    accountsData,
    hoursByUser,
    hoursByProject,
    budgetAlerts,
    incomesData,
    loading,
    error,
    dateRange,
  } = useSelector((state) => state.dashboard);
  const { tasks } = useSelector((state) => state.tasks);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  useEffect(() => {
    if (startDate && endDate) {
      dispatch(fetchDashboardData({ startDate, endDate }));
    }
  }, [dispatch, startDate, endDate]);

  // Format hours by user data for the bar chart
  const barChartData = Object.entries(hoursByUser).flatMap(
    ([userName, dailyHours]) => {
      return Object.entries(dailyHours).map(([day, hours]) => ({
        name: `${userName} - ${day}`,
        hours: hours,
        user: userName,
        day: day,
      }));
    }
  );

  const hoursByProjectData = Object.entries(hoursByProject).map(
    ([name, metrics]) => ({
      name,
      [t('dashboard.charts.hoursByProject.hoursInRange')]: metrics.rangeHours,
      [t('dashboard.charts.hoursByProject.totalHours')]: metrics.totalHours,
      [t('dashboard.charts.hoursByProject.budget')]: metrics.budget,
      [t('dashboard.charts.hoursByProject.billableHours')]:
        metrics.billableHours || 0,
      [t('dashboard.charts.hoursByProject.nonBillableHours')]:
        metrics.nonBillableHours || 0,
      isInternal: metrics.isInternal || false,
    })
  );

  const clientProjectsData = hoursByProjectData.filter(
    (project) => !project.isInternal
  );
  const internalProjectsData = hoursByProjectData.filter(
    (project) => project.isInternal
  );

  // Calculer le nombre de tâches à terminer
  const pendingTasks = tasks.filter((task) => task.status !== 'done').length;

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{t('dashboard.error')}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 3 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box display="flex" gap={2} alignItems="center">
            <DatePicker
              label={t('dashboard.dateRange.startDate')}
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              renderInput={(params) => <TextField {...params} />}
            />
            <DatePicker
              label={t('dashboard.dateRange.endDate')}
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              renderInput={(params) => <TextField {...params} />}
            />
          </Box>
        </LocalizationProvider>
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6} sx={{ width: '75%' }}>
          <Grid container spacing={3}>
            {/* Incomes Chart */}
            <Grid item xs={12} md={6} sx={{ width: '36%' }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {t('dashboard.charts.incomes.title')}
                </Typography>
                <Box height={220}>
                  <ResponsiveContainer>
                    <BarChart data={incomesData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar
                        dataKey="totalIncomes"
                        name={t('dashboard.charts.incomes.totalIncomes')}
                        fill={CHART_COLORS.incomes[0]}
                      />
                      <Bar
                        dataKey="totalBalance"
                        name={t('dashboard.charts.incomes.totalBalance')}
                        fill={CHART_COLORS.incomes[1]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>

            {/* Account Repartition */}
            <Grid item xs={12} md={6} sx={{ width: '20%' }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {t('dashboard.charts.accountRepartition.title')}
                </Typography>
                <Box height={220}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={accountsData}
                        cx="50%"
                        cy="50%"
                        labelLine={true}
                        outerRadius={60}
                        fill={COLORS[2]}
                        dataKey="value"
                        label={({ name, percent }) => {
                          const percentage = (percent * 100).toFixed(0);
                          return percentage > 5 ? `${percentage}%` : '';
                        }}
                      >
                        {accountsData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={
                              CHART_COLORS.accounts[
                                index % CHART_COLORS.accounts.length
                              ]
                            }
                          />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>

            {/* Client Projects Hours */}
            <Grid item xs={12} md={6} sx={{ width: '39%' }}>
              <Paper sx={{ p: 2, height: '100%', width: '100%' }}>
                <Typography variant="h6" gutterBottom>
                  {t('dashboard.charts.hoursByProject.clientProjects')}
                </Typography>
                <Box sx={{ height: 220 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={clientProjectsData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="name"
                        angle={-45}
                        textAnchor="end"
                        height={60}
                        interval={0}
                        tickFormatter={(value) =>
                          value.length > 7
                            ? `${value.substring(0, 7)}...`
                            : value
                        }
                      />
                      <YAxis />
                      <Tooltip />

                      <Bar
                        dataKey={t(
                          'dashboard.charts.hoursByProject.billableHours'
                        )}
                        fill={CHART_COLORS.clientProjects[0]}
                        name={t('projects.billableHours')}
                      />
                      <Bar
                        dataKey={t('dashboard.charts.hoursByProject.budget')}
                        fill={CHART_COLORS.clientProjects[1]}
                        name={t('projects.budgetHours')}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>

            {/* Internal Projects Hours */}
            <Grid item xs={12} md={6} sx={{ width: '40%' }}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="h6" gutterBottom>
                  {t('dashboard.charts.hoursByProject.internalProjects')}
                </Typography>
                <Box sx={{ height: 220 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={internalProjectsData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="name"
                        angle={-45}
                        textAnchor="end"
                        height={60}
                        interval={0}
                        tickFormatter={(value) =>
                          value.length > 7
                            ? `${value.substring(0, 7)}...`
                            : value
                        }
                      />
                      <YAxis />
                      <Tooltip />

                      <Bar
                        dataKey={t(
                          'dashboard.charts.hoursByProject.nonBillableHours'
                        )}
                        fill={CHART_COLORS.internalProjects[0]}
                        name={t('projects.nonBillableHours')}
                      />
                      <Bar
                        dataKey={t('dashboard.charts.hoursByProject.budget')}
                        fill={CHART_COLORS.internalProjects[1]}
                        name={t('projects.budgetHours')}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>

            {/* Hours by User Heat Map */}
            <Grid item xs={12} md={6} sx={{ width: '57%' }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {t('dashboard.charts.hoursByUser.title')}
                </Typography>
                <Box sx={{ overflowX: 'auto' }}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Utilisateur</TableCell>
                          {[
                            'DIM',
                            'LUN',
                            'MAR',
                            'MER',
                            'JEU',
                            'VEN',
                            'SAM',
                          ].map((day) => (
                            <TableCell key={day} align="center">
                              {day}
                            </TableCell>
                          ))}
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                            Total
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(hoursByUser).map(
                          ([userName, dailyHours]) => {
                            // Calculate total hours
                            const totalHours = Object.values(dailyHours).reduce(
                              (sum, hours) => sum + hours,
                              0
                            );

                            return (
                              <TableRow key={userName}>
                                <TableCell component="th" scope="row">
                                  {userName}
                                </TableCell>
                                {[
                                  'DIM',
                                  'LUN',
                                  'MAR',
                                  'MER',
                                  'JEU',
                                  'VEN',
                                  'SAM',
                                ].map((day) => {
                                  const hours = dailyHours[day] || 0;
                                  // Calculate color intensity based on hours (0-8 hours)
                                  const intensity = Math.min(1, hours / 8);
                                  const backgroundColor = `rgba(42, 111, 151, ${
                                    0.2 + intensity * 0.8
                                  })`; // Utilisation de BLUE_PALETTE.MEDIUM
                                  return (
                                    <TableCell
                                      key={`${userName}-${day}`}
                                      align="center"
                                      sx={{
                                        backgroundColor,
                                        color:
                                          intensity > 0.5 ? 'white' : 'black',
                                        fontWeight: 'bold',
                                        minWidth: '60px',
                                      }}
                                    >
                                      {hours.toFixed(1)}
                                    </TableCell>
                                  );
                                })}
                                <TableCell
                                  align="center"
                                  sx={{
                                    backgroundColor: 'rgba(212, 217, 255, 0.3)',
                                    fontWeight: 'bold',
                                    minWidth: '60px',
                                  }}
                                >
                                  {totalHours.toFixed(1)}
                                </TableCell>
                              </TableRow>
                            );
                          }
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} md={6} sx={{ width: '23%' }}>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('dashboard.tasks.pendingTasks')}
            </Typography>
            <Typography variant="h4" color="primary">
              {pendingTasks}
            </Typography>
          </Paper>
          <Paper sx={{ p: 2 }}>
            <ExpensesList startDate={startDate} endDate={endDate} />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardPage;
